import { Link } from '@tanstack/react-router'
import { familyToProvider } from '../../../../../shared-pricing-config'
import { formatPrice } from '../utils/modelsFormatters'
import { isChatModel } from '../types/unified-model.types'
import { ModelDetailSection } from './ModelDetailSection'
import type { ModelFamily } from '../../../../../shared-pricing-config'
import type { ChatModelMetadata, ChatModelPricing, UnifiedModel } from '../types/unified-model.types'
import { ModelFamilyIcon } from '@/shared/components/icons/ModelFamilyIcon'
import { useSpotlight } from '@/shared/hooks/useSpotlight'

interface ModelDetailRelatedProps {
  model: UnifiedModel & { metadata: ChatModelMetadata; pricing: ChatModelPricing }
  allModels: Array<UnifiedModel>
}

const RELATED_LIMIT = 4
const SIMILAR_LIMIT = 3

export function ModelDetailRelated({ model, allModels }: ModelDetailRelatedProps) {
  const chatModels = allModels.filter((m): m is UnifiedModel & { metadata: ChatModelMetadata; pricing: ChatModelPricing } =>
    isChatModel(m) && m.id !== model.id)

  const moreFromFamily = chatModels
    .filter((m) => m.family === model.family)
    .sort((a, b) => Number(b.featured ?? false) - Number(a.featured ?? false))
    .slice(0, RELATED_LIMIT)

  const similarModels = chatModels
    .filter((m) => m.family !== model.family)
    .map((m) => {
      const overlap = m.metadata.capabilities.filter((c) => model.metadata.capabilities.includes(c)).length
      const priceDelta = Math.abs(m.pricing.inputPricePerMillion - model.pricing.inputPricePerMillion)
      return { model: m, overlap, priceDelta }
    })
    .sort((a, b) => b.overlap - a.overlap || a.priceDelta - b.priceDelta)
    .slice(0, SIMILAR_LIMIT)
    .map((entry) => entry.model)

  if (moreFromFamily.length === 0 && similarModels.length === 0) return null

  return (
    <ModelDetailSection title="Related models">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {moreFromFamily.length > 0 && (
          <div>
            <h3 className="eyebrow mb-3">
              More from {familyToProvider(model.family)}
            </h3>
            <div className="flex flex-col gap-2">
              {moreFromFamily.map((m) => <RelatedModelCard key={m.id} model={m} />)}
            </div>
          </div>
        )}
        {similarModels.length > 0 && (
          <div>
            <h3 className="eyebrow mb-3">Similar Models</h3>
            <div className="flex flex-col gap-2">
              {similarModels.map((m) => <RelatedModelCard key={m.id} model={m} />)}
            </div>
          </div>
        )}
      </div>
    </ModelDetailSection>
  )
}

function RelatedModelCard({ model }: { model: UnifiedModel & { metadata: ChatModelMetadata; pricing: ChatModelPricing } }) {
  const onPointerMove = useSpotlight()
  return (
    <Link
      to="/models/$type/$"
      params={{ type: 'chat', _splat: model.id }}
      onPointerMove={onPointerMove}
      className="surface-bevel spotlight flex items-center gap-3 rounded-[1.25rem] px-4 py-3.5 transition-colors duration-300 ease-brisk hover:border-[var(--color-border-strong)]"
    >
      <ModelFamilyIcon modelFamily={model.family as ModelFamily} size={24} />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[var(--color-text-primary)] truncate">{model.name}</p>
        <p className="font-mono text-xs tabular-nums text-[var(--color-text-tertiary)]">
          Input {formatPrice(model.pricing.inputPricePerMillion)}/M
        </p>
      </div>
    </Link>
  )
}
